import { Box, Typography } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import {
  BodyCopy,
  Eyebrow,
  Frame,
  NavText,
  TitleRow,
  TitleWord,
  CutButton,
  ButtonLabel,
  IntroDesktopShell,
  IntroLimeCta,
} from "./IntroPrimitives";
import { ga, useIsDesktop } from "../../components/ui/GaPrimitives";

export const introSwipeVariants = {
  enter: (dir) => ({ x: dir >= 0 ? "100%" : "-100%" }),
  center: { x: 0 },
  exit: (dir) => ({ x: dir >= 0 ? "-100%" : "100%" }),
};

export const introSwipeTransition = { duration: 0.45, ease: "easeOut" };

function SwipePane({ slideKey, direction, children, fill = false, sx }) {
  return (
    <Box sx={{ overflow: "hidden", width: "100%", position: "relative", ...sx }}>
      <AnimatePresence mode="wait" custom={direction} initial={false}>
        <motion.div
          key={slideKey}
          custom={direction}
          variants={introSwipeVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={introSwipeTransition}
          style={
            fill
              ? { position: "absolute", inset: 0, width: "100%", height: "100%" }
              : { width: "100%" }
          }
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </Box>
  );
}

function ProgressDots({ active, size = "mobile" }) {
  const desktop = size === "desktop";
  const activeW = desktop ? 28 : 18;
  const idleW = desktop ? 10 : 12;
  const height = desktop ? 4 : 3;
  const gap = desktop ? 8 : 6;
  const count = 4;

  return (
    <Box
      sx={{
        position: "relative",
        height,
        width: activeW + idleW * (count - 1) + gap * (count - 1),
        flexShrink: 0,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: `${gap}px`, height }}>
        {Array.from({ length: count }, (_, i) => (
          <Box
            key={i}
            component={motion.div}
            initial={false}
            animate={{ width: i === active ? activeW : idleW }}
            transition={introSwipeTransition}
            sx={{
              height,
              flexShrink: 0,
              bgcolor: "#2A2E22",
            }}
          />
        ))}
      </Box>
      <Box
        component={motion.div}
        initial={false}
        animate={{ x: active * (idleW + gap) }}
        transition={introSwipeTransition}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: activeW,
          height,
          bgcolor: ga.lime,
        }}
      />
    </Box>
  );
}

function DesktopIntro({ slide, onBack, onSkip, onContinue, direction }) {
  return (
    <IntroDesktopShell
      onSkip={onSkip}
      onBack={onBack}
      showSkip={slide.showSkip}
      showBack={slide.showBack}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          px: "20px",
          display: "grid",
          gridTemplateColumns: {
            md: "minmax(0, 1fr) minmax(0, 1fr)",
            lg: "minmax(0, 1.05fr) minmax(0, 0.95fr)",
          },
          columnGap: { md: 6, lg: 10, xl: 12 },
          rowGap: 4,
          alignItems: "center",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "100%",
            justifySelf: "stretch",
          }}
        >
          <SwipePane slideKey={slide.key} direction={direction}>
            <Box
              component="img"
              src={slide.art}
              alt=""
              loading="eager"
              decoding="async"
              fetchPriority="high"
              sx={{
                width: "100%",
                height: "auto",
                maxHeight: { md: "48vh", lg: "56vh", xl: 580 },
                display: "block",
                objectFit: "contain",
              }}
            />
          </SwipePane>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            maxWidth: "100%",
            minWidth: 0,
            justifySelf: { md: "center", lg: "start" },
          }}
        >
          <SwipePane slideKey={slide.key} direction={direction}>
            <Typography
              sx={{
                fontFamily: "'Rajdhani', sans-serif",
                fontWeight: 700,
                fontSize: { md: 13, lg: 14 },
                letterSpacing: "3px",
                color: ga.lime,
                lineHeight: 1.3,
                mb: { md: 1.5, lg: 2 },
              }}
            >
              {slide.eyebrow}
            </Typography>
            <Box sx={{ mb: { md: 2, lg: 2.5 } }}>
              <Typography
                sx={{
                  fontFamily: "'Rajdhani', sans-serif",
                  fontWeight: 700,
                  fontSize: { md: 40, lg: 48, xl: 54 },
                  letterSpacing: "1px",
                  lineHeight: 1.12,
                  color: ga.white,
                }}
              >
                {slide.title[0]}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Rajdhani', sans-serif",
                  fontWeight: 700,
                  fontSize: { md: 40, lg: 48, xl: 54 },
                  letterSpacing: "1px",
                  lineHeight: 1.12,
                  color: ga.lime,
                }}
              >
                {slide.title[1]}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 400,
                fontSize: { md: 15, lg: 16, xl: 17 },
              lineHeight: 1.55,
              color: ga.muted,
              maxWidth: 480,
            }}
          >
            {slide.body}
          </Typography>
        </SwipePane>
        <Box sx={{ mt: { md: 3.5, lg: 4.5 } }}>
          <IntroLimeCta onClick={onContinue} width={240} height={48}>
            {slide.cta}
          </IntroLimeCta>
        </Box>
        <Box sx={{ height: { md: 28, lg: 36 } }} />
          <ProgressDots active={slide.activeDot} size="desktop" />
        </Box>
      </Box>
    </IntroDesktopShell>
  );
}

export default function IntroCarousel({ slide, onBack, onSkip, onContinue, direction = 1 }) {
  const isDesktop = useIsDesktop();

  if (isDesktop) {
    return (
      <DesktopIntro
        slide={slide}
        onBack={onBack}
        onSkip={onSkip}
        onContinue={onContinue}
        direction={direction}
      />
    );
  }

  return (
    <Frame
      sx={{
        background: "#0D0F0A",
        width: "100%",
        maxWidth: "100%",
        height: "100%",
        maxHeight: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        boxSizing: "border-box",
        px: "16px",
        pt: "max(16px, env(safe-area-inset-top))",
        pb: "max(72px, calc(env(safe-area-inset-bottom) + 48px))",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flex: "1 1 auto", minHeight: 0, width: "100%", overflow: "hidden" }}>
        <SwipePane slideKey={slide.key} direction={direction} fill sx={{ height: "100%" }}>
          <Box
            sx={{
              height: "100%",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                width: "100%",
                minHeight: 24,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexShrink: 0,
              }}
            >
              {slide.showBack ? (
                <NavText onClick={onBack} onMouseDown={(event) => event.preventDefault()}>
                  ‹&nbsp;&nbsp;BACK
                </NavText>
              ) : (
                <Box />
              )}
              {slide.showSkip ? <NavText onClick={onSkip}>SKIP</NavText> : <Box />}
            </Box>

            <Box
              sx={{
                flex: "1 1 auto",
                minHeight: 0,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Box sx={{ flex: "1 1 16px", minHeight: 8 }} />
              <Box
                component="img"
                src={slide.art}
                alt=""
                loading="eager"
                decoding="async"
                fetchPriority="high"
                sx={{
                  width: "auto",
                  height: "auto",
                  maxWidth: "min(260px, 72%)",
                  maxHeight: "min(280px, 34vh)",
                  flexShrink: 0,
                  display: "block",
                  objectFit: "contain",
                }}
              />
              <Box
                sx={{
                  width: "100%",
                  flexShrink: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                  mt: "18px",
                }}
              >
                <Eyebrow>{slide.eyebrow}</Eyebrow>
                <TitleRow sx={{ flexWrap: "wrap", maxWidth: "100%", px: "4px" }}>
                  <TitleWord sx={{ fontSize: "clamp(18px, 6vw, 26px)" }}>{slide.title[0]}</TitleWord>
                  <TitleWord accent sx={{ fontSize: "clamp(18px, 6vw, 26px)" }}>
                    {slide.title[1]}
                  </TitleWord>
                </TitleRow>
                <BodyCopy sx={{ width: "100%", maxWidth: 340, boxSizing: "border-box" }}>
                  {slide.body}
                </BodyCopy>
              </Box>
              <Box sx={{ flex: "1 1 auto", minHeight: 12 }} />
            </Box>
          </Box>
        </SwipePane>
      </Box>

      <Box
        sx={{
          width: "100%",
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px",
          mt: "16px",
        }}
      >
        <ProgressDots active={slide.activeDot} />
        <CutButton onClick={onContinue}>
          <ButtonLabel>{slide.cta}</ButtonLabel>
        </CutButton>
      </Box>
    </Frame>
  );
}
